/**
 * `RangePresets` — segmented control for `?range` on the metrics
 * overview endpoint. Five fixed presets per the admin metrics wire
 * contract (`AdminMetricsOverview.range`): 24h / 7d / 30d / 90d / 1y.
 *
 * Visually: a thin-bordered button row in the admin's mono/uppercase
 * idiom; the active preset wears a lime accent border + lime text so
 * the choice is unmistakable on screen-shares. Keyboard-navigable
 * (`<button>`s with `aria-pressed`).
 */

import type { ReportingRange } from '@sill/types';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

const PRESETS: ReadonlyArray<{ value: ReportingRange; label: string }> = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
];

export interface RangePresetsProps {
  value: ReportingRange;
  onChange: (next: ReportingRange) => void;
  disabled?: boolean;
}

export function RangePresets({ value, onChange, disabled }: RangePresetsProps): JSX.Element {
  const C = useC();
  return (
    <div
      role="group"
      aria-label="Date range"
      className="inline-flex"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: 2,
      }}
    >
      {PRESETS.map((preset) => {
        const active = preset.value === value;
        return (
          <button
            key={preset.value}
            type="button"
            onClick={() => {
              if (!active) onChange(preset.value);
            }}
            disabled={disabled}
            aria-pressed={active}
            className="px-3 py-1.5"
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: active ? C.limeFg : C.muted,
              background: active ? C.limeDim : 'transparent',
              border: `1px solid ${active ? C.limeFg : 'transparent'}`,
              borderRadius: 2,
              cursor: active || disabled === true ? 'default' : 'pointer',
              opacity: disabled === true ? 0.6 : 1,
              minWidth: 44,
            }}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}
